import React, { useState } from 'react';
import { claimsApi } from '../../firebase/functionsService';
import { useTerms } from '../../hooks/useTerms';
import { waitingMinutes, waitingAmount, bomturAmount } from '../../utils/terms';
import { toDate } from '../../utils/orderCardFormat';
import { overtimeAmount, effectiveHourlyRate } from '../../utils/orderHourly';
import { COLORS } from '../../styles/tokens';

const ACTIVE_STATUSES = ['matched', 'in_progress', 'delivered_pending'];
const STATUS_LABEL = { pending: 'Venter på bestiller', approved: 'Godkjent', disputed: 'Bestridt' };
const MAX_NOTE = 300;
const MAX_OVERTIME_CLAIMS = 3; // som serveren, inkludert avviste

const fmt = n => Number(n).toLocaleString('nb-NO');
const clock = d => d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
const latest = (a, b) => (a && b ? (a > b ? a : b) : a || b);

// Krav som ikke er avvist: hindrer ny innsending for samme trinn
function liveClaim(order, kind, stage) {
  return (order.claims ?? []).find(c => c.kind === kind && (c.stage ?? null) === stage && c.status !== 'rejected');
}

// Sjåførens knapper på et tildelt oppdrag: «Jeg er fremme», «Krev ventetid» og «Rapporter bomtur».
// Beløpene her er veiledning; serveren regner det endelige beløpet.
export default function ClaimsPanel({ order }) {
  const { terms } = useTerms();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [bomturOpen, setBomturOpen] = useState(false);
  const [note, setNote] = useState('');
  const [overtimeOpen, setOvertimeOpen] = useState(false);
  const [otMinutes, setOtMinutes] = useState('');
  const [otNote, setOtNote] = useState('');

  if (!order || !ACTIVE_STATUSES.includes(order.status)) return null;

  async function run(fn) {
    if (busy) return false;
    setBusy(true);
    setError('');
    try {
      await fn();
      return true;
    } catch (err) {
      console.error('ClaimsPanel:', err);
      setError(err?.message || 'Noe gikk galt. Prøv igjen.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  const scheduled = toDate(order.scheduledAt);
  const arrivedPickup = toDate(order.arrivedAtPickup);
  const arrivedDelivery = toDate(order.arrivedAtDelivery);
  const pickupDone = toDate(order.pickupRegisteredAt);
  const deliveredAt = toDate(order.deliveredAt);

  const stages = [
    {
      stage: 'pickup', word: 'hentested', title: 'Henting',
      arrived: arrivedPickup,
      canArrive: order.status === 'matched',
      minutes: waitingMinutes({ arrivedAt: latest(arrivedPickup, scheduled), doneAt: pickupDone }, terms),
      done: !!pickupDone,
    },
    {
      stage: 'delivery', word: 'leveringssted', title: 'Levering',
      arrived: arrivedDelivery,
      canArrive: order.status === 'in_progress',
      minutes: waitingMinutes({ arrivedAt: latest(arrivedDelivery, pickupDone), doneAt: deliveredAt }, terms),
      done: !!deliveredAt,
      hidden: order.status === 'matched',
    },
  ].filter(s => !s.hidden);

  const base = order.agreedPrice ?? order.fixedPrice ?? order.suggestedPrice ?? 0;
  let bomtur = bomturAmount(base, terms);
  if (base > 0) bomtur = Math.min(bomtur, Math.round(base));
  const arrivedNow = order.status === 'matched' ? arrivedPickup : order.status === 'in_progress' ? arrivedDelivery : null;
  const bomturClaim = liveClaim(order, 'bomtur', null);

  function sendBomtur() {
    const text = note.trim();
    if (!text) { setError('Skriv en begrunnelse for bomturen.'); return; }
    run(async () => {
      await claimsApi.submitClaim({ orderId: order.id, kind: 'bomtur', note: text });
      setBomturOpen(false);
      setNote('');
    });
  }

  // Overtid gjelder bare timeoppdrag. Beløpet her er et anslag; serveren regner det endelige fra ordrens timepris.
  const overtimeClaims = (order.claims ?? []).filter(c => c.kind === 'overtime');
  const overtimeStarted = ['in_progress', 'delivered_pending'].includes(order.status); // som serveren: ikke i matched
  const canClaimOvertime = !!order.hourly && overtimeStarted && overtimeClaims.length < MAX_OVERTIME_CLAIMS;
  const otEstimate = order.hourly ? overtimeAmount(otMinutes.trim() === '' ? NaN : Number(otMinutes), effectiveHourlyRate(order)) : null;

  function closeOvertime() {
    setOvertimeOpen(false);
    setOtMinutes('');
    setOtNote('');
    setError('');
  }

  function sendOvertime() {
    const minutes = otMinutes.trim() === '' ? NaN : Number(otMinutes);
    if (!Number.isInteger(minutes) || minutes < 1 || minutes > 240) { setError('Overtid må være mellom 1 og 240 minutter.'); return; }
    const text = otNote.trim();
    if (!text) { setError('Skriv en beskrivelse av overtiden.'); return; }
    run(async () => {
      await claimsApi.submitClaim({ orderId: order.id, kind: 'overtime', minutes, note: text });
      closeOvertime();
    });
  }

  return (
    <div style={styles.box}>
      {stages.map(s => {
        const claim = liveClaim(order, 'waiting', s.stage);
        const amount = waitingAmount(s.minutes, terms);
        return (
          <div key={s.stage} style={styles.row}>
            <div style={styles.title}>{s.title}</div>
            {s.arrived ? (
              <div style={styles.info}>Fremme kl. {clock(s.arrived)}</div>
            ) : s.canArrive ? (
              <button type="button" style={styles.primary} disabled={busy}
                onClick={() => run(() => claimsApi.registerArrival({ orderId: order.id, stage: s.stage }))}>
                Jeg er fremme ved {s.word}
              </button>
            ) : null}
            {claim ? (
              <div style={styles.info}>
                Ventetid {fmt(claim.minutes ?? 0)} min · {fmt(claim.amount ?? 0)} kr: <span style={styles.status}>{STATUS_LABEL[claim.status] ?? claim.status}</span>
              </div>
            ) : s.arrived && s.done && s.minutes > 0 ? (
              <div>
                <div style={styles.info}>{`${fmt(s.minutes)} min · ${fmt(amount)} kr`}</div>
                <button type="button" style={styles.secondary} disabled={busy}
                  onClick={() => run(() => claimsApi.submitClaim({ orderId: order.id, kind: 'waiting', stage: s.stage }))}>
                  Krev ventetid
                </button>
              </div>
            ) : null}
          </div>
        );
      })}

      {arrivedNow && (bomturClaim ? (
        <div style={styles.row}>
          <div style={styles.info}>
            Bomtur {fmt(bomturClaim.amount ?? 0)} kr: <span style={styles.status}>{STATUS_LABEL[bomturClaim.status] ?? bomturClaim.status}</span>
          </div>
        </div>
      ) : bomturOpen ? (
        <div style={styles.row}>
          <label htmlFor="bomtur-note" style={styles.title}>Begrunnelse</label>
          <textarea id="bomtur-note" value={note} maxLength={MAX_NOTE} rows={3} style={styles.textarea}
            placeholder="Hvorfor kunne du ikke laste eller losse?"
            onChange={e => setNote(e.target.value)} />
          <div style={styles.info}>Bomtur: {fmt(bomtur)} kr</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" style={styles.primary} disabled={busy} onClick={sendBomtur}>Send krav</button>
            <button type="button" style={styles.secondary} disabled={busy} onClick={() => { setBomturOpen(false); setError(''); }}>Avbryt</button>
          </div>
        </div>
      ) : (
        <div style={styles.row}>
          <button type="button" style={styles.secondary} disabled={busy} onClick={() => setBomturOpen(true)}>Rapporter bomtur</button>
        </div>
      ))}

      {order.hourly && (
        <div style={styles.row}>
          {overtimeClaims.map(c => (
            <div key={c.id} style={styles.info}>
              Overtid {fmt(c.minutes ?? 0)} min · {fmt(c.amount ?? 0)} kr: <span style={styles.status}>{STATUS_LABEL[c.status] ?? c.status}</span>
            </div>
          ))}
          {overtimeOpen ? (
            <>
              <label htmlFor="overtime-minutes" style={styles.title}>Minutter overtid</label>
              <input id="overtime-minutes" type="number" inputMode="numeric" min={1} max={240} step={1} value={otMinutes}
                style={styles.input} placeholder="F.eks. 45"
                onChange={e => setOtMinutes(e.target.value)} />
              <label htmlFor="overtime-note" style={styles.title}>Beskrivelse av overtiden</label>
              <textarea id="overtime-note" value={otNote} maxLength={MAX_NOTE} rows={3} style={styles.textarea}
                placeholder="Hvorfor tok oppdraget lengre tid enn bestilt?"
                onChange={e => setOtNote(e.target.value)} />
              {otEstimate != null && (
                <div style={styles.info}>Anslått beløp: {fmt(otEstimate)} kr (minutter rundes opp til 15)</div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" style={styles.primary} disabled={busy} onClick={sendOvertime}>Send overtidskrav</button>
                <button type="button" style={styles.secondary} disabled={busy} onClick={closeOvertime}>Avbryt</button>
              </div>
            </>
          ) : canClaimOvertime ? (
            <button type="button" style={styles.secondary} disabled={busy} onClick={() => setOvertimeOpen(true)}>Krev overtid</button>
          ) : !overtimeStarted ? (
            <div style={styles.info}>Overtid kan kreves når oppdraget er i gang.</div>
          ) : (
            <div style={styles.info}>Du har sendt maks {MAX_OVERTIME_CLAIMS} overtidskrav. Kontakt Tippi om mer.</div>
          )}
        </div>
      )}

      {error && <p role="alert" style={styles.error}>{error}</p>}
    </div>
  );
}

const styles = {
  box:       { background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: 14, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 },
  row:       { display: 'flex', flexDirection: 'column', gap: 8 },
  title:     { fontSize: 12, fontWeight: 700, color: COLORS.textLight, textTransform: 'uppercase', letterSpacing: 0.3 },
  info:      { fontSize: 14, color: COLORS.textDark },
  status:    { fontWeight: 700 },
  primary:   { height: 44, borderRadius: 22, border: 'none', background: COLORS.primary, color: COLORS.onPrimary, fontWeight: 700, fontSize: 14, cursor: 'pointer', padding: '0 18px' },
  secondary: { height: 40, borderRadius: 20, border: `1px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textDark, fontWeight: 600, fontSize: 14, cursor: 'pointer', padding: '0 16px' },
  input:     { width: '100%', boxSizing: 'border-box', borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: 10, fontSize: 14, fontFamily: 'inherit' },
  textarea:  { width: '100%', boxSizing: 'border-box', borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: 10, fontSize: 14, fontFamily: 'inherit' },
  error:     { background: COLORS.dangerBg, color: COLORS.dangerText, padding: '10px 14px', borderRadius: 6, fontSize: 14, margin: 0 },
};
